import {useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {RTC_PROTOCOL_V1, type CallIntent, type GroupCallState} from '@phonehq/rtc-core';
import {matrixService} from '../../../services/matrixService';
import {isServiceParticipant} from '../../../utils/aiBotUtils';
import {groupCallService} from '../../groupCalls/services/groupCallService';
import {saveDevicePreferences} from '../../groupCalls/services/devicePreferences';
import {runCallControl} from '../../groupCalls/components/groupCallActions';
import type {GroupCallNotice} from '../../groupCalls/services/groupCallOrchestrator';
import {PrepDialog, type PrepBanner, type PrepPerson} from './PrepDialog';
import {usePrepMedia} from './usePrepMedia';
import {useSelfIdentity} from './useSelfIdentity';
import {PRESENCE_TEXT, usePrepPresence} from './usePrepPresence';
import {prepMessages as m} from './prepMessages';

interface GroupPrepProps {
    readonly state: GroupCallState;
    readonly roomName: string;
    readonly notice?: GroupCallNotice;
    readonly preparing: boolean;
    readonly videoAvailable: boolean;
}

interface RoomMemberView {
    readonly userId: string;
    readonly name: string;
}

const roomMembersOf = (roomId: string | undefined, localUserId: string | undefined): readonly RoomMemberView[] =>
    (roomId ? matrixService.getRoom(roomId)?.getJoinedMembers() ?? [] : [])
        .filter(member => member.userId !== localUserId && !isServiceParticipant(member.userId))
        .map(member => ({userId: member.userId, name: member.name || member.userId}))
        .sort((left, right) => left.name.localeCompare(right.name));

export const GroupPrep = ({state, roomName, notice, preparing, videoAvailable}: GroupPrepProps) => {
    const intl = useIntl();
    const localUserId = groupCallService.localUserId;
    const [intent, setIntent] = useState<CallIntent>(videoAvailable ? state.intent : 'audio');
    const [deselected, setDeselected] = useState<ReadonlySet<string>>(new Set());
    const members = useMemo(() => roomMembersOf(state.roomId, localUserId), [localUserId, state.roomId]);
    const memberIds = useMemo(() => members.map(member => member.userId), [members]);
    const presence = usePrepPresence(memberIds);
    const selfIdentity = useSelfIdentity();
    const media = usePrepMedia(intent, videoAvailable);
    const inCall = useMemo(
        () => new Set(state.lobbyParticipants.filter(view => !view.isLocal && !isServiceParticipant(view.userId)).map(view => view.userId)),
        [state.lobbyParticipants],
    );
    const joiningExisting = inCall.size > 0;
    const inCallNames = intl.formatList([...inCall].map(userId => members.find(member => member.userId === userId)?.name ?? userId), {type: 'conjunction'});
    const starter = useMemo(
        () => [...state.lobbyParticipants]
            .filter(view => !view.isLocal && !isServiceParticipant(view.userId))
            .filter(view => view.joinedAt !== undefined)
            .sort((left, right) => (left.joinedAt ?? 0) - (right.joinedAt ?? 0))[0],
        [state.lobbyParticipants],
    );
    const selected = memberIds.filter(userId => !deselected.has(userId));
    const max = RTC_PROTOCOL_V1.maxParticipants;
    const overLimit = selected.length + 1 > max;

    const people: PrepPerson[] = members.map(member => {
        const status = presence.get(member.userId) ?? 'offline';
        return {
            id: member.userId,
            identity: {name: member.name, userId: member.userId},
            presence: intl.formatMessage(inCall.has(member.userId) ? m.presenceInCall : PRESENCE_TEXT[status]),
            selected: !deselected.has(member.userId),
            selectable: !joiningExisting,
        };
    });

    const toggle = (userId: string) => setDeselected(current => {
        const next = new Set(current);
        if (next.has(userId)) {
            next.delete(userId);
        } else {
            next.add(userId);
        }
        return next;
    });

    const allSelected = deselected.size === 0;
    const toggleAll = () => setDeselected(allSelected ? new Set(memberIds) : new Set());

    const banner: PrepBanner | undefined = notice
        ? {tone: 'danger', title: intl.formatMessage(m.errorTitle), body: intl.formatMessage(m.errorBody), testId: 'group-call-lobby-notice'}
        : joiningExisting
            ? {
                  tone: 'info',
                  title: intl.formatMessage(m.alreadyTitle),
                  body: starter
                      ? intl.formatMessage(m.alreadyBodyStarter, {
                            name: members.find(member => member.userId === starter.userId)?.name ?? starter.userId,
                            minutes: Math.max(0, Math.round((Date.now() - (starter.joinedAt ?? Date.now())) / 60_000)),
                            names: inCallNames,
                        })
                      : intl.formatMessage(m.alreadyBody, {names: inCallNames}),
              }
            : overLimit
                ? {tone: 'warning', title: intl.formatMessage(m.limitTitle), body: intl.formatMessage(m.limitBody, {max}), testId: 'group-call-lobby-full'}
                : media.issues.microphone
                    ? {
                          tone: 'warning',
                          title: intl.formatMessage(m.micBlockedTitle),
                          body: intl.formatMessage(m.micBlockedBody),
                          action: {label: intl.formatMessage(m.micHowTo), onClick: media.retryMicrophone},
                          testId: 'group-call-lobby-device-issues',
                      }
                    : undefined;

    const start = () => {
        saveDevicePreferences(media.devices);
        groupCallService.clearNotice();
        void runCallControl('join', () => groupCallService.join({
            intent,
            microphoneEnabled: media.microphoneEnabled,
            cameraEnabled: media.cameraEnabled,
            devices: media.devices,
            targets: joiningExisting ? undefined : selected,
        }));
    };

    const cancel = () => {
        groupCallService.clearNotice();
        void runCallControl('cancel', () => groupCallService.leave());
    };

    return (
        <PrepDialog
            subtitle={intl.formatMessage(m.groupSubtitle, {room: roomName, count: members.length + 1})}
            intent={intent}
            videoAvailable={videoAvailable}
            onIntent={setIntent}
            people={joiningExisting ? [] : people}
            onTogglePerson={joiningExisting ? undefined : toggle}
            onToggleAll={joiningExisting || members.length < 2 ? undefined : toggleAll}
            microphone={media.microphone}
            camera={media.camera}
            preview={media.preview}
            selfIdentity={selfIdentity}
            banner={banner}
            footerNote={joiningExisting ? intl.formatMessage(m.alreadyCount, {count: inCall.size}) : undefined}
            primary={{
                label: intl.formatMessage(joiningExisting ? m.joinCall : notice ? m.tryAgain : m.start),
                busy: preparing,
                disabled: !joiningExisting && (overLimit || selected.length === 0),
                onClick: start,
            }}
            onCancel={cancel}
        />
    );
};
